/**
 * Copywriting library page
 */

import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Table, Space, Tag, message, Dropdown, Menu, Tree, Modal } from 'antd';
import { PlusOutlined, MoreOutlined, EditOutlined, DeleteOutlined, FolderOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { copywritingApi, folderApi } from '../../services/resourceService';
import { getCurrentUser } from '../../services/auth';
import { getUsers } from '../../services/user';
import { Copywriting, ResourceFolder, ResourceScope, FolderType } from '../../types/resource';
import CreateCopywritingModal from './CreateCopywritingModal';
import EditCopywritingModal from './EditCopywritingModal';
import CreateFolderModal from './CreateFolderModal';
import EditFolderModal from './EditFolderModal';
import './Copywriting.css';

const { Search } = Input;
const { Option } = Select;
const { confirm } = Modal;

// Build tree structure from flat folder list
const buildFolderTree = (folders: ResourceFolder[], onEdit: (folder: ResourceFolder) => void, onDelete: (folder: ResourceFolder) => void) => {
  const folderMap = new Map<number, any>();
  const rootFolders: any[] = [];

  // Create tree nodes
  folders.forEach((folder) => {
    folderMap.set(folder.id, {
      key: folder.id,
      title: (
        <div className="folder-tree-node">
          <span className="folder-name">{folder.name}</span>
          <Dropdown
            overlay={
              <Menu>
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    onEdit(folder);
                  }}
                >
                  编辑
                </Menu.Item>
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  danger
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    onDelete(folder);
                  }}
                >
                  删除
                </Menu.Item>
              </Menu>
            }
            trigger={['click']}
          >
            <MoreOutlined
              className="folder-actions"
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </div>
      ),
      icon: <FolderOutlined />,
      folder: folder,
      children: [],
    });
  });

  // Build tree structure
  folders.forEach((folder) => {
    const node = folderMap.get(folder.id);
    if (folder.parent_id && folderMap.has(folder.parent_id)) {
      const parent = folderMap.get(folder.parent_id);
      parent.children.push(node);
    } else {
      rootFolders.push(node);
    }
  });

  return rootFolders;
};

const CopywritingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ResourceScope>(ResourceScope.ENTERPRISE);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Copywriting[]>([]);
  const [total, setTotal] = useState(0);
  const [folders, setFolders] = useState<ResourceFolder[]>([]);
  const [folderTree, setFolderTree] = useState<any[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedCopywriting, setSelectedCopywriting] = useState<Copywriting | null>(null);
  const [folderModalVisible, setFolderModalVisible] = useState(false);
  const [editFolderModalVisible, setEditFolderModalVisible] = useState(false);
  const [editingFolder, setEditingFolder] = useState<ResourceFolder | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [currentUser, setCurrentUser] = useState<any>(null);

  // Load folders
  const loadFolders = async () => {
    try {
      const result = await folderApi.getList({
        folder_type: 'copywriting',
        scope: activeTab,
        owner_id: selectedUserId,
      });
      setFolders(result.items);
      // Build tree structure with edit/delete handlers
      const tree = buildFolderTree(
        result.items,
        (folder) => {
          setEditingFolder(folder);
          setEditFolderModalVisible(true);
        },
        handleDeleteFolder
      );
      setFolderTree(tree);
      // Auto expand all folders
      const allKeys = result.items.map(f => f.id);
      setExpandedKeys(allKeys);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Load copywriting data
  const loadData = async () => {
    setLoading(true);
    try {
      const result = await copywritingApi.getList({
        page,
        page_size: pageSize,
        scope: activeTab,
        folder_id: selectedFolder || undefined,
        owner_id: selectedUserId,
        keyword: searchKeyword || undefined,
      });
      setData(result.items);
      setTotal(result.total);
    } catch (error) {
      message.error('加载失败');
    } finally {
      setLoading(false);
    }
  };

  // Load users (for admin)
  const loadUsers = async () => {
    try {
      const result = await getUsers({ skip: 0, limit: 1000 });
      // Backend returns array directly, not { items: [] } format
      const userList = Array.isArray(result) ? result : (result.items || []);
      setUsers(userList);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  // Load current user
  const loadCurrentUser = async () => {
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);
      // If admin, load users list
      if (user.role === 'admin') {
        loadUsers();
      }
    } catch (error) {
      console.error('Failed to load current user:', error);
    }
  };

  // Initial load
  useEffect(() => {
    loadCurrentUser();
  }, []);

  // Load data when params change
  useEffect(() => {
    loadData();
  }, [page, pageSize, activeTab, selectedFolder, searchKeyword, selectedUserId]);

  // Load folders when tab changes
  useEffect(() => {
    loadFolders();
    setSelectedFolder(null);
    setSelectedUserId(undefined);
    setPage(1);
  }, [activeTab]);

  // Reload folders when selected user changes (for admin viewing other users' personal folders)
  useEffect(() => {
    if (activeTab === ResourceScope.PERSONAL && selectedUserId !== undefined) {
      loadFolders();
      setSelectedFolder(null);
    }
  }, [selectedUserId]);

  // Handle delete
  const handleDelete = async (id: number) => {
    try {
      await copywritingApi.delete(id);
      message.success('删除成功');
      loadData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  // Handle delete folder
  const handleDeleteFolder = async (folder: ResourceFolder) => {
    confirm({
      title: '确认删除',
      icon: <ExclamationCircleOutlined />,
      content: `确定要删除分组"${folder.name}"吗？`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await folderApi.delete(folder.id);
          message.success('删除成功');
          loadFolders();
          if (selectedFolder === folder.id) {
            setSelectedFolder(null);
          }
        } catch (error: any) {
          message.error(error.response?.data?.detail || '删除失败');
        }
      },
    });
  };

  // Table columns
  const columns = [
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 150,
      ellipsis: {
        showTitle: true,
      },
      render: (title: string) => title || '-',
    },
    {
      title: '文案内容',
      dataIndex: 'content',
      key: 'content',
      width: 200,
      ellipsis: {
        showTitle: true,
      },
      render: (content: string) => (
        <span title={content}>{content}</span>
      ),
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags: string[]) => {
        if (!tags || tags.length === 0) return '-';
        return (
          <Space size={4} wrap>
            {tags.map((tag, index) => (
              <Tag key={index} color="blue">{tag}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '发送次数',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 100,
      sorter: true,
      render: (count: number) => (
        <span style={{ color: count > 0 ? '#1890ff' : undefined }}>
          {count}
        </span>
      ),
    },
    {
      title: '创建人',
      dataIndex: 'owner_name',
      key: 'owner_name',
      width: 100,
      render: (name: string) => name || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 80,
      fixed: 'right' as const,
      render: (_: any, record: Copywriting) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item 
                key="edit" 
                icon={<EditOutlined />}
                onClick={() => {
                  setSelectedCopywriting(record);
                  setEditModalVisible(true);
                }}
              >
                编辑
              </Menu.Item>
              <Menu.Item
                key="delete"
                icon={<DeleteOutlined />}
                danger
                onClick={() => handleDelete(record.id)}
              >
                删除
              </Menu.Item>
            </Menu>
          }
          trigger={['click']}
        >
          <Button type="text" size="small" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="copywriting-page">
      {/* Header with title and tabs */}
      <div className="page-header">
        <div className="header-left">
          <h2>文案库</h2>
          <div className="scope-tabs">
            <div
              className={`tab-item ${activeTab === ResourceScope.ENTERPRISE ? 'active' : ''}`}
              onClick={() => setActiveTab(ResourceScope.ENTERPRISE)}
            >
              企业文案
            </div>
            <div
              className={`tab-item ${activeTab === ResourceScope.PERSONAL ? 'active' : ''}`}
              onClick={() => setActiveTab(ResourceScope.PERSONAL)}
            >
              个人文案
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Left sidebar - Folders */}
        <div className="folder-sidebar">
          <div className="folder-header">
            <span>分组</span>
            <Button 
              type="text" 
              size="small" 
              icon={<PlusOutlined />}
              onClick={() => setFolderModalVisible(true)}
            >
              添加分组
            </Button>
          </div>
          <div className="folder-list">
            <div
              className={`folder-item ${selectedFolder === null ? 'active' : ''}`}
              onClick={() => setSelectedFolder(null)}
            >
              <span>全部分组</span>
              <span className="folder-count">{total}</span>
            </div>
            {folderTree.length > 0 && (
              <Tree
                showIcon
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys)}
                selectedKeys={selectedFolder ? [selectedFolder] : []}
                onSelect={(keys) => {
                  if (keys.length > 0) {
                    setSelectedFolder(keys[0] as number);
                  }
                }}
                treeData={folderTree}
                className="folder-tree"
              />
            )}
          </div>
        </div>

        {/* Right content - Table */}
        <div className="main-content">
          {/* Toolbar */}
          <div className="content-toolbar">
            <Space>
              <Search
                placeholder="搜索文案内容..."
                allowClear
                style={{ width: 300 }}
                onSearch={setSearchKeyword}
              />
              {activeTab === ResourceScope.PERSONAL && currentUser?.role === 'admin' && (
                <Select
                  placeholder="选择用户"
                  allowClear
                  style={{ width: 150 }}
                  value={selectedUserId}
                  onChange={(value) => setSelectedUserId(value)}
                >
                  {users && users.map((user) => (
                    <Option key={user.id} value={user.id}>
                      {user.real_name || user.username}
                    </Option>
                  ))}
                </Select>
              )}
              <Select
                value={sortBy}
                onChange={setSortBy}
                style={{ width: 150 }}
              >
                <Option value="created_at">按创建时间</Option>
                <Option value="usage_count">按发送次数</Option>
                <Option value="updated_at">按更新时间</Option>
              </Select>
            </Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              新增文案
            </Button>
          </div>

          {/* Table */}
          <Table
            loading={loading}
            dataSource={data}
            columns={columns}
            rowKey="id"
            scroll={{ x: 1200, y: 'calc(100vh - 380px)' }}
            pagination={{
              current: page,
              pageSize: pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPage(page);
                setPageSize(pageSize);
              },
            }}
          />
        </div>
      </div>

      {/* Create Modal */}
      <CreateCopywritingModal
        visible={createModalVisible}
        scope={activeTab}
        onCancel={() => setCreateModalVisible(false)}
        onSuccess={() => {
          setCreateModalVisible(false);
          loadData();
        }}
      />

      {/* Edit Modal */}
      <EditCopywritingModal
        visible={editModalVisible}
        copywriting={selectedCopywriting}
        onCancel={() => {
          setEditModalVisible(false);
          setSelectedCopywriting(null);
        }}
        onSuccess={() => {
          setEditModalVisible(false);
          setSelectedCopywriting(null);
          loadData();
        }}
      />

      {/* Folder Modal */}
      <CreateFolderModal
        visible={folderModalVisible}
        folderType={FolderType.COPYWRITING}
        scope={activeTab}
        onCancel={() => setFolderModalVisible(false)}
        onSuccess={() => {
          setFolderModalVisible(false);
          loadFolders();
        }}
      />

      {/* Edit Folder Modal */}
      <EditFolderModal
        visible={editFolderModalVisible}
        folder={editingFolder}
        allFolders={folders}
        onCancel={() => {
          setEditFolderModalVisible(false);
          setEditingFolder(null);
        }}
        onSuccess={() => {
          setEditFolderModalVisible(false);
          setEditingFolder(null);
          loadFolders();
        }}
      />
    </div>
  );
};

export default CopywritingPage;
